// src/pages/AgentLogin.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, generateToken, storeCredentials } from '../../utils/api';


// ✅ API_BASE - No trailing spaces
const API_BASE = (process.env.REACT_APP_API_URL);

// 🎨 Orange Theme Colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_DARK = '#ea7c1f';

function AgentLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // ✅ Load remembered email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('agentRememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const payload = {
        Email: formData.email.trim().toLowerCase(),
        Password: formData.password
      };

      console.log('📤 Agent Login Request:', { ...payload, Password: '***' });
      console.log('🔍 API URL:', `${API_BASE}/Settings/agentlogin`);

      const response = await authenticatedFetch(
        `${API_BASE}/Settings/agentlogin`,
        'POST',
        payload
      );

      console.log('✅ Agent Login Response..:', response);

      // ✅ Handle success
      if (response?.success === true) {
        // ✅ Store agent session data
        const sessionData = {
          agentId: response.agentId,
          companyId: response.companyId,
          userEmail: formData.email.toLowerCase().trim(),
          fullName: response.data?.fullName || response.data?.FullName || '',
          agentType: response.data?.agentType || response.data?.AgentType || '',
          phoneNumber: response.data?.phoneNumber || response.data?.PhoneNumber || '',
          isActive: response.data?.isActive !== false,
          expiresAt: response.expiresInSeconds 
            ? Date.now() + (response.expiresInSeconds * 1000) 
            : Date.now() + (7200 * 1000), // 2 hours default
          message: response.message,
          loggedInAt: new Date().toISOString()
        };
        
        // ✅ Save to localStorage - use 'agentSession' key
        localStorage.setItem('agentSession', JSON.stringify(sessionData));
        console.log('💾 Stored agent session:', sessionData);
        
        // ✅ Remember email if checked
        if (rememberMe) {
          localStorage.setItem('agentRememberedEmail', formData.email);
        } else {
          localStorage.removeItem('agentRememberedEmail');
        }
        
        // ✅ Pre-generate API credentials
        try {
          const credentials = await generateToken();
          storeCredentials(credentials);
          console.log('✅ Agent API credentials stored');
        } catch (tokenErr) {
          console.warn('⚠️ Agent token generation warning:', tokenErr);
        }
        
        // ✅ Show success
        toast.success(response.message || 'Welcome back, Agent!');
        
        // ✅ Redirect to agent dashboard
        setTimeout(() => {
          navigate('/agent-dashboard', { 
            replace: true,
            state: { 
              fromLogin: true, 
              agentId: response.agentId
            }
          });
        }, 500);
        
      } else {
        // ✅ Handle business logic errors
        const errorMsg = response?.message || 'Login failed. Please try again.';
        
        if (errorMsg.toLowerCase().includes('invalid')) {
          setErrors({ submit: 'Invalid email or password. Please try again.' });
          toast.error('Invalid credentials');
        } else if (errorMsg.toLowerCase().includes('inactive') || errorMsg.toLowerCase().includes('disabled')) {
          setErrors({ submit: 'Your agent account is inactive. Please contact support.' });
          toast.error('Account inactive');
        } else {
          setErrors({ submit: errorMsg });
          toast.error(errorMsg);
        }
      }
      
    } catch (error) {
      console.error('💥 Agent Login Error:', { 
        message: error.message, 
        name: error.name,
        response: error.response
      });
      
      const errorResponse = error.response?.data || error.data || {};
      const errorMsg = errorResponse?.message || error.message || 'An error occurred. Please try again.';
      
      let msg = 'An error occurred. Please try again.';
      
      if (error.message?.includes('Missing headers') || 
          error.message?.includes('AuthorizationKey') ||
          error.message?.includes('Token') ||
          error.message?.includes('SecretKey')) {
        msg = 'Authentication configuration error. Please contact support.';
      } else if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        msg = 'Cannot connect to server. Check your internet connection.';
      } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        msg = errorMsg.toLowerCase().includes('invalid') ? 'Invalid credentials' : 'Authentication failed';
      } else if (error.message?.includes('404')) {
        msg = 'API endpoint not found. Please contact support.';
      } else {
        msg = errorMsg;
      }
      
      setErrors({ submit: msg });
      toast.error(msg);
      
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleLogout = (e) => {
    e?.preventDefault();
    localStorage.removeItem('agentSession');
    localStorage.removeItem('agentRememberedEmail');
    toast.success('Logged out successfully');
    navigate('/agent-login', { replace: true });
  };

  return (
    <div className="agent-login-page">
      <ToastContainer position="top-right" autoClose={4000} theme="light" />

      {/* Header */}
      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <Link to="/" className="brand-logo" aria-label="FSL HMO Home">
              <img src="images/fsl_logo.png" alt="FSL HMO Logo" style={{ height: '50px' }} />
            </Link>
            <button className="theme-btn style-two" onClick={() => navigate('/')} disabled={isLoading} aria-label="Back to Home">← Back to Home</button>
          </div>
        </div>
      </header>

      {/* Login Form */}
      <section className="login-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-7">
              <div className="login-container">
                <div className="login-header text-center mb-4">
                  <h1 className="login-title mb-2">Agent Portal</h1>
                  <p className="login-subtitle">Login to manage your sales and commissions</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form" noValidate>
                  {/* Error Alert */}
                  {errors.submit && (
                    <div className="alert alert-danger mb-3 border-0" role="alert" aria-live="polite">
                      <i className="fas fa-exclamation-circle me-2" aria-hidden="true"></i>
                      {errors.submit}
                    </div>
                  )}

                  {/* Email */}
                  <div className="form-group mb-3">
                    <label htmlFor="email" className="form-label">Email address <span className="text-danger">*</span></label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`} 
                      placeholder="Enter your agent email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      disabled={isLoading} 
                      autoComplete="email" 
                      required 
                      aria-invalid={!!errors.email} 
                    />
                    {errors.email && <div className="invalid-feedback" role="alert">{errors.email}</div>}
                  </div>

                  {/* Password */}
                  <div className="form-group mb-3">
                    <label htmlFor="password" className="form-label">Password <span className="text-danger">*</span></label>
                    <div className="password-input-wrapper">
                      <input 
                        type={showPassword ? 'text' : 'password'} 
                        id="password" 
                        name="password" 
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`} 
                        placeholder="Enter your password" 
                        value={formData.password} 
                        onChange={handleChange} 
                        disabled={isLoading} 
                        autoComplete="current-password" 
                        required 
                        aria-invalid={!!errors.password} 
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn" 
                        onClick={togglePasswordVisibility} 
                        disabled={isLoading} 
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <i className="fas fa-eye-slash" aria-hidden="true"></i> : <i className="fas fa-eye" aria-hidden="true"></i>}
                      </button>
                    </div>
                    {errors.password && <div className="invalid-feedback" role="alert">{errors.password}</div>}
                    
                    <div className="text-end mt-2">
                      <a href="/" className="forgot-password-link" onClick={(e) => { e.preventDefault(); toast.info('Contact admin for password reset'); }}>
                        Forgot Password?
                      </a>
                    </div>
                  </div>

                  {/* Remember Me */}
                  <div className="form-check mb-3">
                    <input 
                      type="checkbox" 
                      className="form-check-input" 
                      id="rememberMe" 
                      checked={rememberMe} 
                      onChange={(e) => setRememberMe(e.target.checked)} 
                      disabled={isLoading} 
                    />
                    <label className="form-check-label" htmlFor="rememberMe">Remember me on this device</label>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    className="btn btn-login w-100 mt-4 mb-3" 
                    disabled={isLoading} 
                    aria-busy={isLoading}
                    style={{ backgroundColor: BRAND_ORANGE, borderColor: BRAND_ORANGE }}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Logging in...
                      </>
                    ) : (
                      <>
                        Login <i className="fas fa-arrow-right ms-2" aria-hidden="true"></i>
                      </>
                    )}
                  </button>

                  {/* Sign Up / Contact */}
                  <div className="text-center mt-3">
                    <p className="mb-0 text-muted">
                      Not an agent yet? <a href="/get-started" className="signup-link">Apply now</a>
                    </p>
                    <p className="mb-0 text-muted mt-2">
                      Need help? <a href="/support" className="signup-link">Contact Support</a>
                    </p>
                  </div>

                  {/* Security */}
                  <div className="text-center mt-4 pt-3 border-top">
                    <small className="text-muted">
                      <i className="fas fa-shield-alt me-1" aria-hidden="true"></i>
                      Your connection is secure and encrypted
                    </small>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CSS */}
      <style>{`
        .agent-login-page { min-height: 100vh; background-color: #fafafa; display: flex; flex-direction: column; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .login-section { flex: 1; display: flex; align-items: center; }
        .login-container { background: white; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.08); padding: 40px; width: 100%; animation: slideUp 0.5s ease; border-top: 4px solid ${BRAND_ORANGE}; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        
        .login-header { margin-bottom: 30px; }
        .login-title { font-size: 28px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
        .login-subtitle { color: #6b7280; font-size: 14px; margin: 0; }
        .form-label { font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 8px; display: block; }
        .form-control { padding: 12px 16px; border: 1px solid #d1d5db; border-radius: 10px; font-size: 14px; transition: all 0.3s ease; background-color: #f9fafb; width: 100%; }
        .form-control:focus { border-color: ${BRAND_ORANGE}; box-shadow: 0 0 0 4px rgba(248,148,35,0.15); background-color: white; outline: none; }
        .form-control.is-invalid { border-color: #ef4444; background-color: #fef2f2; }
        .form-control.is-invalid:focus { box-shadow: 0 0 0 4px rgba(239,68,68,0.15); }
        .form-control:disabled { background-color: #f3f4f6; cursor: not-allowed; opacity: 0.8; }
        .invalid-feedback { color: #ef4444; font-size: 12px; margin-top: 4px; display: block; }
        .password-input-wrapper { position: relative; }
        .password-toggle-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #6b7280; cursor: pointer; padding: 8px; transition: color 0.3s ease; display: flex; align-items: center; justify-content: center; }
        .password-toggle-btn:hover:not(:disabled) { color: ${BRAND_ORANGE}; }
        .password-toggle-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .forgot-password-link, .signup-link { color: ${BRAND_ORANGE}; text-decoration: none; font-size: 13px; font-weight: 500; transition: color 0.3s ease; }
        .forgot-password-link:hover, .signup-link:hover { color: ${BRAND_ORANGE_DARK}; text-decoration: underline; }
        .form-check-input { width: 18px; height: 18px; border-color: #d1d5db; cursor: pointer; accent-color: ${BRAND_ORANGE}; }
        .form-check-label { font-size: 14px; color: #4b5563; margin-left: 8px; cursor: pointer; }
        .btn-login { background: linear-gradient(135deg, ${BRAND_ORANGE} 0%, ${BRAND_ORANGE_DARK} 100%); border: none; color: white; padding: 14px 20px; font-size: 15px; font-weight: 600; border-radius: 10px; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; width: 100%; }
        .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(248,148,35,0.4); }
        .btn-login:disabled { opacity: 0.75; cursor: not-allowed; transform: none; }
        .alert { padding: 12px 16px; border-radius: 10px; font-size: 14px; display: flex; align-items: flex-start; }
        .alert-danger { background-color: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .alert-danger i { margin-top: 2px; }
        .spinner-border { width: 1rem; height: 1rem; border-width: 0.15em; vertical-align: text-bottom; border-color: currentColor transparent currentColor transparent !important; }
        @media (max-width: 768px) { .login-container { padding: 30px 20px; border-radius: 12px; } .login-title { font-size: 24px; } }
        @media (max-width: 480px) { .login-section { padding: 20px 0; } .login-container { padding: 25px 15px; } .btn-login { padding: 12px; font-size: 14px; } }
        .form-control:focus-visible, .btn-login:focus-visible, .password-toggle-btn:focus-visible, .forgot-password-link:focus-visible { outline: 2px solid ${BRAND_ORANGE}; outline-offset: 2px; }
      `}</style>
    </div>
  );
}

export default AgentLogin;